import type { ITheme } from '@inter-ikea-kompis/types';

import SkapaDelayEnum from './enums/SkapaDelayEnum';
import SkapaDurationEnum from './enums/SkapaDurationEnum';
import SkapaFontSizeEnum from './enums/SkapaFontSizeEnum';
import SkapaIconDurationEnum from './enums/SkapaIconDurationEnum';

export default <ITheme>{
	/** @deprecated Use Skapas way of scaling instead. */
	scale: 1,
	color: {
		accent: {
			primaryAccent: '#0058a3',
			primaryAccentDark: '#004f93',
			primaryAccentDarkest: '#003e72',
			secondaryAccent: '#ffdb00',
			secondaryAccentDark: '#e6c200',
			secondaryAccentDarker: '#ccad00'
		},
		neutral: {
			white: '#ffffff',
			grey100: '#f5f5f5',
			grey200: '#dfdfdf',
			grey300: '#cccccc',
			grey500: '#929292',
			grey700: '#484848',
			grey900: '#111111'
		},
		commercialMessage: {
			newLowerPrice: '#cc0008',
			new: '#ca5008',
			btiYellow: '#ffdb00',
			btiRed: '#cc0008',
			family: '#007cc1'
		},
		semantic: {
			positive: '#0a8a00',
			negative: '#e00751',
			caution: '#f26a2f',
			informative: '#0058a3'
		},
		component: {
			dangerDefault: '#e00751',
			dangerHover: '#cc003d',
			dangerPress: '#b80029',
			primaryDefault: '#111111',
			primaryHover: '#333333',
			primaryPress: '#000000',
			inverseBorderPress: '#ffffff',
			primaryEmphasisedDefault: '#0058a3',
			primaryEmphasisedHover: '#004f93',
			primaryEmphasisedPress: '#003e72',
			switchOff: '#808080'
		},
		background: {
			default: '#ffffff',
			alt: '#f5f5f5',
			inverse: '#111111',
			accent: '#0058a3'
		},
		text: {
			default: '#484848',
			dark: '#111111',
			lightest: '#767676',
			inverse: '#ffffff'
		},
		border: {
			default: '#929292',
			dark: '#111111',
			light: '#dfdfdf',
			inverse: '#ffffff'
		},
		icon: {
			default: '#111111',
			favorite: '#e00751',
			light: '#929292',
			inverse: '#ffffff'
		},
		disabled: {
			default: '#dfdfdf'
		},
		transparency: {
			default: 'rgba(255,255,255,0)',
			light: 'rgba(0,0,0,0.15)',
			dark: 'rgba(0,0,0,0.7)'
		},
		campaign: {
			sustainability: '#00853e'
		}
	},
	border: {
		thickness: {
			thin: 1,
			thick: 2
		},
		radius: {
			radius4: 4,
			radius8: 8,
			radius12: 12,
			radius20: 20,
			radius64: 64
		}
	},
	fontFace: {
		family: 'notoIKEAlatin',
		fallbacks: ['Noto Sans', 'Roboto', 'Open Sans', 'sans-serif'],
		sources: [
			{
				formats: {
					ttf: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-BoldItalic.ttf',
						format: 'truetype'
					},
					woff: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-BoldItalic.woff',
						format: 'woff'
					},
					woff2: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-BoldItalic.woff2',
						format: 'woff2'
					}
				},
				weight: 700,
				style: 'italic'
			},
			{
				formats: {
					ttf: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-Bold.ttf',
						format: 'truetype'
					},
					woff: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-Bold.woff',
						format: 'woff'
					},
					woff2: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-Bold.woff2',
						format: 'woff2'
					}
				},
				weight: 700,
				style: 'normal'
			},
			{
				formats: {
					ttf: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-Italic.ttf',
						format: 'truetype'
					},
					woff: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-Italic.woff',
						format: 'woff'
					},
					woff2: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-Italic.woff2',
						format: 'woff2'
					}
				},
				weight: 400,
				style: 'italic'
			},
			{
				formats: {
					ttf: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-Regular.ttf',
						format: 'truetype'
					},
					woff: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-Regular.woff',
						format: 'woff'
					},
					woff2: {
						url: '/addon-app/components/resources/fonts/44/noto/NotoSansIKEA-Regular.woff2',
						format: 'woff2'
					}
				},
				weight: 400,
				style: 'normal'
			}
		]
	},
	focusOutline: {
		offset: 4,
		width: 2,
		color: '#111111',
		transitionTime: 0.2,
		borderRadius: 2
	},
	spacing: {
		space25: 4,
		space50: 8,
		space75: 12,
		space100: 16,
		space125: 20,
		space150: 24,
		space200: 32,
		space250: 40,
		space300: 48,
		space400: 64,
		space550: 88,
		space750: 120
	},
	elevation: {
		size: {
			default: '0px 4px 16px'
		},
		color: {
			default: 'rgba(0,0,0,0.1)'
		}
	},
	motion: {
		ease: {
			easy: 'cubic-bezier(0.4, 0.0, 0.4, 1.0)',
			expressive: 'cubic-bezier(0.8, 0.0, 0.0, 1.0)',
			enter: 'cubic-bezier(0.0, 0.0, 0.1, 1.0)',
			exit: 'cubic-bezier(0.9, 0.0, 1.0, 1.0)',
			bob: 'cubic-bezier(0.45, 1.45, 0.65, 1.0)',
			linear: 'cubic-bezier(0,0,1,1)'
		},
		duration: {
			xsmall: SkapaDurationEnum.instant,
			small: SkapaDurationEnum.quick,
			medium: SkapaDurationEnum.standard,
			large: SkapaDurationEnum.slow
		},
		iconDuration: {
			small: SkapaIconDurationEnum.simple,
			medium: SkapaIconDurationEnum.average,
			large: SkapaIconDurationEnum.complex
		},
		delay: {
			small: SkapaDelayEnum.tight,
			medium: SkapaDelayEnum.standard,
			large: SkapaDelayEnum.emphasised
		}
	},
	breakpoint: {
		small: {
			width: 0,
			gridSpacing: {
				column: 4,
				gutter: 12
			},
			fontStyle: {
				bodyXLarge: { size: SkapaFontSizeEnum.fontSize300, lineHeight: 32 },
				bodyLarge: { size: SkapaFontSizeEnum.fontSize100, lineHeight: 26 },
				bodyMedium: { size: SkapaFontSizeEnum.fontSize75, lineHeight: 22 },
				bodySmall: { size: SkapaFontSizeEnum.fontSize50, lineHeight: 18 },
				headingXSmall: {
					size: SkapaFontSizeEnum.fontSize75,
					lineHeight: 22,
					weight: 700
				},
				headingSmall: {
					size: SkapaFontSizeEnum.fontSize100,
					lineHeight: 24,
					weight: 700
				},
				headingMedium: {
					size: SkapaFontSizeEnum.fontSize200,
					lineHeight: 26,
					weight: 700
				},
				headingLarge: {
					size: SkapaFontSizeEnum.fontSize500,
					lineHeight: 34,
					letterSpacing: -0.42,
					weight: 700
				},
				headingXLarge: {
					size: SkapaFontSizeEnum.fontSize700,
					lineHeight: 44,
					letterSpacing: -0.56,
					weight: 700
				},
				priceXSmall: { size: SkapaFontSizeEnum.fontSize50, lineHeight: 12 },
				priceSmall: { size: SkapaFontSizeEnum.fontSize75, lineHeight: 14 },
				priceMedium: { size: SkapaFontSizeEnum.fontSize200, lineHeight: 18 },
				priceLarge: { size: SkapaFontSizeEnum.fontSize500, lineHeight: 25 },
				priceXLarge: { size: SkapaFontSizeEnum.fontSize600, lineHeight: 28 },
				priceXXLarge: { size: SkapaFontSizeEnum.fontSize1000, lineHeight: 45 }
			}
		},
		medium: {
			width: 600,
			gridSpacing: {
				column: 6,
				gutter: 16
			},
			fontStyle: {
				bodyXLarge: { size: SkapaFontSizeEnum.fontSize300, lineHeight: 32 },
				bodyLarge: { size: SkapaFontSizeEnum.fontSize100, lineHeight: 26 },
				bodyMedium: { size: SkapaFontSizeEnum.fontSize75, lineHeight: 22 },
				bodySmall: { size: SkapaFontSizeEnum.fontSize50, lineHeight: 18 },
				headingXSmall: {
					size: SkapaFontSizeEnum.fontSize75,
					lineHeight: 22,
					weight: 700
				},
				headingSmall: {
					size: SkapaFontSizeEnum.fontSize100,
					lineHeight: 24,
					weight: 700
				},
				headingMedium: {
					size: SkapaFontSizeEnum.fontSize200,
					lineHeight: 26,
					weight: 700
				},
				headingLarge: {
					size: SkapaFontSizeEnum.fontSize500,
					lineHeight: 34,
					letterSpacing: -0.42,
					weight: 700
				},
				headingXLarge: {
					size: SkapaFontSizeEnum.fontSize700,
					lineHeight: 44,
					letterSpacing: -0.56,
					weight: 700
				},
				priceXSmall: { size: SkapaFontSizeEnum.fontSize50, lineHeight: 12 },
				priceSmall: { size: SkapaFontSizeEnum.fontSize75, lineHeight: 14 },
				priceMedium: { size: SkapaFontSizeEnum.fontSize200, lineHeight: 18 },
				priceLarge: { size: SkapaFontSizeEnum.fontSize500, lineHeight: 25 },
				priceXLarge: { size: SkapaFontSizeEnum.fontSize600, lineHeight: 28 },
				priceXXLarge: { size: SkapaFontSizeEnum.fontSize1000, lineHeight: 45 }
			}
		},
		large: {
			width: 900,
			gridSpacing: {
				column: 12,
				gutter: 24
			},
			fontStyle: {
				bodyXLarge: { size: SkapaFontSizeEnum.fontSize300, lineHeight: 32 },
				bodyLarge: { size: SkapaFontSizeEnum.fontSize100, lineHeight: 26 },
				bodyMedium: { size: SkapaFontSizeEnum.fontSize75, lineHeight: 22 },
				bodySmall: { size: SkapaFontSizeEnum.fontSize50, lineHeight: 18 },
				headingXSmall: {
					size: SkapaFontSizeEnum.fontSize75,
					lineHeight: 22,
					weight: 700
				},
				headingSmall: {
					size: SkapaFontSizeEnum.fontSize100,
					lineHeight: 24,
					weight: 700
				},
				headingMedium: {
					size: SkapaFontSizeEnum.fontSize200,
					lineHeight: 26,
					weight: 700
				},
				headingLarge: {
					size: SkapaFontSizeEnum.fontSize500,
					lineHeight: 34,
					letterSpacing: -0.42,
					weight: 700
				},
				headingXLarge: {
					size: SkapaFontSizeEnum.fontSize700,
					lineHeight: 44,
					letterSpacing: -0.56,
					weight: 700
				},
				priceXSmall: { size: SkapaFontSizeEnum.fontSize50, lineHeight: 12 },
				priceSmall: { size: SkapaFontSizeEnum.fontSize75, lineHeight: 14 },
				priceMedium: { size: SkapaFontSizeEnum.fontSize200, lineHeight: 18 },
				priceLarge: { size: SkapaFontSizeEnum.fontSize500, lineHeight: 25 },
				priceXLarge: { size: SkapaFontSizeEnum.fontSize600, lineHeight: 28 },
				priceXXLarge: { size: SkapaFontSizeEnum.fontSize1000, lineHeight: 45 }
			}
		},
		xlarge: {
			width: 1200,
			gridSpacing: {
				column: 12,
				gutter: 24
			},
			fontStyle: {
				bodyXLarge: { size: SkapaFontSizeEnum.fontSize300, lineHeight: 32 },
				bodyLarge: { size: SkapaFontSizeEnum.fontSize100, lineHeight: 26 },
				bodyMedium: { size: SkapaFontSizeEnum.fontSize75, lineHeight: 22 },
				bodySmall: { size: SkapaFontSizeEnum.fontSize50, lineHeight: 18 },
				headingXSmall: {
					size: SkapaFontSizeEnum.fontSize75,
					lineHeight: 22,
					weight: 700
				},
				headingSmall: {
					size: SkapaFontSizeEnum.fontSize100,
					lineHeight: 24,
					weight: 700
				},
				headingMedium: {
					size: SkapaFontSizeEnum.fontSize500,
					lineHeight: 34,
					letterSpacing: -0.42,
					weight: 700
				},
				headingLarge: {
					size: SkapaFontSizeEnum.fontSize800,
					lineHeight: 48,
					letterSpacing: -0.63,
					weight: 700
				},
				headingXLarge: {
					size: SkapaFontSizeEnum.fontSize1100,
					lineHeight: 66,
					letterSpacing: -0.87,
					weight: 700
				},
				priceXSmall: { size: SkapaFontSizeEnum.fontSize50, lineHeight: 12 },
				priceSmall: { size: SkapaFontSizeEnum.fontSize75, lineHeight: 14 },
				priceMedium: { size: SkapaFontSizeEnum.fontSize200, lineHeight: 18 },
				priceLarge: { size: SkapaFontSizeEnum.fontSize500, lineHeight: 25 },
				priceXLarge: { size: SkapaFontSizeEnum.fontSize600, lineHeight: 28 },
				priceXXLarge: { size: SkapaFontSizeEnum.fontSize1000, lineHeight: 45 }
			}
		},
		xxlarge: {
			width: 1920,
			gridSpacing: {
				column: 12,
				gutter: 24
			},
			fontStyle: {
				bodyXLarge: { size: SkapaFontSizeEnum.fontSize300, lineHeight: 32 },
				bodyLarge: { size: SkapaFontSizeEnum.fontSize100, lineHeight: 26 },
				bodyMedium: { size: SkapaFontSizeEnum.fontSize75, lineHeight: 22 },
				bodySmall: { size: SkapaFontSizeEnum.fontSize50, lineHeight: 18 },
				headingXSmall: {
					size: SkapaFontSizeEnum.fontSize75,
					lineHeight: 22,
					weight: 700
				},
				headingSmall: {
					size: SkapaFontSizeEnum.fontSize100,
					lineHeight: 24,
					weight: 700
				},
				headingMedium: {
					size: SkapaFontSizeEnum.fontSize500,
					lineHeight: 34,
					letterSpacing: -0.42,
					weight: 700
				},
				headingLarge: {
					size: SkapaFontSizeEnum.fontSize800,
					lineHeight: 48,
					letterSpacing: -0.63,
					weight: 700
				},
				headingXLarge: {
					size: SkapaFontSizeEnum.fontSize1100,
					lineHeight: 66,
					letterSpacing: -0.87,
					weight: 700
				},
				priceXSmall: { size: SkapaFontSizeEnum.fontSize50, lineHeight: 12 },
				priceSmall: { size: SkapaFontSizeEnum.fontSize75, lineHeight: 14 },
				priceMedium: { size: SkapaFontSizeEnum.fontSize200, lineHeight: 18 },
				priceLarge: { size: SkapaFontSizeEnum.fontSize500, lineHeight: 25 },
				priceXLarge: { size: SkapaFontSizeEnum.fontSize600, lineHeight: 28 },
				priceXXLarge: { size: SkapaFontSizeEnum.fontSize1000, lineHeight: 45 }
			}
		}
	}
};
